import Component from "@glimmer/component";
import { fn } from "@ember/helper";
import { action } from "@ember/object";
import { service } from "@ember/service";
import DButton from "discourse/components/d-button";
import DropdownMenu from "discourse/components/dropdown-menu";
import DMenu from "discourse/float-kit/components/d-menu";
import concatClass from "discourse/helpers/concat-class";
import icon from "discourse/helpers/d-icon";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import getURL from "discourse/lib/get-url";
import DiscourseURL from "discourse/lib/url";
import { and, not, or } from "discourse/truth-helpers";

export default class TopicAdminMenu extends Component {
  @service adminTopicMenuButtons;
  @service currentUser;
  @service siteSettings;

  @action
  onRegisterApi(api) {
    this.dMenu = api;
  }

  @action
  async onButtonAction(buttonAction) {
    await this.dMenu.close();
    this.args[buttonAction]?.();
  }

  @action
  async onExtraButtonAction(buttonAction) {
    await this.dMenu.close();
    buttonAction?.();
  }

  get extraButtons() {
    return this.adminTopicMenuButtons.callbacks
      .map((callback) => {
        return callback(this.args.topic);
      })
      .filter(Boolean);
  }

  get details() {
    return this.args.topic.get("details");
  }

  get isPrivateMessage() {
    return this.args.topic.get("isPrivateMessage");
  }

  get featured() {
    return (
      !!this.args.topic.get("pinned_at") || this.args.topic.get("isBanner")
    );
  }

  get visible() {
    return this.args.topic.get("visible");
  }

  get canDelete() {
    return this.details.get("can_delete");
  }

  get canRecover() {
    return this.details.get("can_recover");
  }

  get archived() {
    return this.args.topic.get("archived");
  }

  get topicModerationHistoryUrl() {
    return getURL(`/review?topic_id=${this.args.topic.id}&status=all`);
  }

  get showAdminButton() {
    return (
      this.currentUser?.canManageTopic ||
      this.details?.can_archive_topic ||
      this.details?.can_close_topic ||
      this.details?.can_split_merge_topic
    );
  }

  get showNestedRepliesToggle() {
    return (
      this.siteSettings.nested_replies_enabled &&
      !this.siteSettings.nested_replies_default &&
      this.currentUser?.staff
    );
  }

  get nestedRepliesToggleLabel() {
    return this.args.topic.get("is_nested_view")
      ? "nested_replies.topic_admin_menu.disable_nested_replies"
      : "nested_replies.topic_admin_menu.enable_nested_replies";
  }

  @action
  async toggleNestedReplies() {
    await this.dMenu.close();
    const topic = this.args.topic;
    const newValue = !topic.get("is_nested_view");
    const topicId = topic.get("id");
    const slug = topic.get("slug");

    try {
      await ajax(`/n/${slug}/${topicId}/toggle`, {
        type: "PUT",
        data: { enabled: newValue },
      });
      topic.set("is_nested_view", newValue);

      if (newValue) {
        DiscourseURL.routeTo(`/n/${slug}/${topicId}`);
      } else {
        DiscourseURL.routeTo(`/t/${slug}/${topicId}`);
      }
    } catch (e) {
      popupAjaxError(e);
    }
  }

  <template>
    {{#if this.showAdminButton}}
      <DMenu
        @identifier="topic-admin-menu"
        @onRegisterApi={{this.onRegisterApi}}
        @modalForMobile={{true}}
        @autofocus={{true}}
        @triggerClass="btn-default btn-icon toggle-admin-menu {{@buttonClasses}}"
      >
        <:trigger>
          {{icon "wrench"}}
        </:trigger>
        <:content>
          <DropdownMenu as |dropdown|>
            {{#if
              (or
                this.currentUser.canManageTopic
                this.details.can_split_merge_topic
              )
            }}
              <dropdown.item class="topic-admin-multi-select">
                <DButton
                  @label="topic.actions.multi_select"
                  @action={{fn this.onButtonAction "toggleMultiSelect"}}
                  @icon="list-check"
                />
              </dropdown.item>
            {{/if}}

            {{#if
              (or
                this.currentUser.canManageTopic
                this.details.can_moderate_category
              )
            }}
              {{#if this.canDelete}}
                <dropdown.item class="topic-admin-delete">
                  <DButton
                    @label="topic.actions.delete"
                    @action={{fn this.onButtonAction "deleteTopic"}}
                    @icon="trash-can"
                    class="popup-menu-btn-danger --danger"
                  />
                </dropdown.item>
              {{else if this.canRecover}}
                <dropdown.item class="topic-admin-recover">
                  <DButton
                    @label="topic.actions.recover"
                    @action={{fn this.onButtonAction "recoverTopic"}}
                    @icon="arrow-rotate-left"
                  />
                </dropdown.item>
              {{/if}}
            {{/if}}

            {{#if this.details.can_close_topic}}
              <dropdown.item
                class={{if
                  @topic.closed
                  "topic-admin-open"
                  "topic-admin-close"
                }}
              >
                <DButton
                  @label={{if
                    @topic.closed
                    "topic.actions.open"
                    "topic.actions.close"
                  }}
                  @action={{fn this.onButtonAction "toggleClosed"}}
                  @icon={{if @topic.closed "topic.opened" "topic.closed"}}
                />
              </dropdown.item>
            {{/if}}

            {{#if
              (and
                this.details.can_pin_unpin_topic
                (not this.isPrivateMessage)
                (or this.visible this.featured this.details.can_banner_topic)
              )
            }}
              <dropdown.item class="topic-admin-pin">
                <DButton
                  @label={{if
                    this.featured
                    "topic.actions.unpin"
                    "topic.actions.pin"
                  }}
                  @action={{fn this.onButtonAction "showFeatureTopic"}}
                  @icon="thumbtack"
                />
              </dropdown.item>
            {{/if}}

            {{#if
              (and this.details.can_archive_topic (not this.isPrivateMessage))
            }}
              <dropdown.item class="topic-admin-archive">
                <DButton
                  @label={{if
                    this.archived
                    "topic.actions.unarchive"
                    "topic.actions.archive"
                  }}
                  @action={{fn this.onButtonAction "toggleArchived"}}
                  @icon="folder"
                />
              </dropdown.item>
            {{/if}}

            {{#if this.details.can_toggle_topic_visibility}}
              <dropdown.item class="topic-admin-visible">
                <DButton
                  @label={{if
                    this.visible
                    "topic.actions.invisible"
                    "topic.actions.visible"
                  }}
                  @action={{fn this.onButtonAction "toggleVisibility"}}
                  @icon={{if this.visible "far-eye-slash" "far-eye"}}
                />
              </dropdown.item>
            {{/if}}

            {{#if (and this.details.can_convert_topic)}}
              <dropdown.item class="topic-admin-convert">
                <DButton
                  @label={{if
                    this.isPrivateMessage
                    "topic.actions.make_public"
                    "topic.actions.make_private"
                  }}
                  @action={{fn
                    this.onButtonAction
                    (if
                      this.isPrivateMessage
                      "convertToPublicTopic"
                      "convertToPrivateMessage"
                    )
                  }}
                  @icon={{if this.isPrivateMessage "comment" "envelope"}}
                />
              </dropdown.item>
            {{/if}}

            <dropdown.divider />

            {{#if this.currentUser.canManageTopic}}
              <dropdown.item class="admin-topic-timer-update">
                <DButton
                  @label="topic.actions.timed_update"
                  @action={{fn this.onButtonAction "showTopicTimerModal"}}
                  @icon="far-clock"
                />
              </dropdown.item>

              {{#if this.currentUser.staff}}
                <dropdown.item class="topic-admin-change-timestamp">
                  <DButton
                    @label="topic.change_timestamp.title"
                    @action={{fn this.onButtonAction "showChangeTimestamp"}}
                    @icon="calendar-days"
                  />
                </dropdown.item>
              {{/if}}

              <dropdown.item class="topic-admin-reset-bump-date">
                <DButton
                  @label="topic.actions.reset_bump_date"
                  @action={{fn this.onButtonAction "resetBumpDate"}}
                  @icon="anchor"
                />
              </dropdown.item>

              <dropdown.item class="topic-admin-slow-mode">
                <DButton
                  @label="topic.actions.slow_mode"
                  @action={{fn this.onButtonAction "showTopicSlowModeUpdate"}}
                  @icon="hourglass-start"
                />
              </dropdown.item>
            {{/if}}

            {{#if
              (or
                this.currentUser.staff
                this.showNestedRepliesToggle
                this.extraButtons.length
              )
            }}
              <dropdown.divider />

              {{#if this.currentUser.staff}}
                <dropdown.item class="topic-admin-moderation-history">
                  <DButton
                    @label="review.moderation_history"
                    @href={{this.topicModerationHistoryUrl}}
                    @icon="list"
                  />
                </dropdown.item>
              {{/if}}

              {{#if this.showNestedRepliesToggle}}
                <dropdown.item class="topic-admin-nested-replies">
                  <DButton
                    @label={{this.nestedRepliesToggleLabel}}
                    @action={{this.toggleNestedReplies}}
                    @icon="nested-thread"
                  />
                </dropdown.item>
              {{/if}}

              {{#each this.extraButtons as |button|}}
                <dropdown.item>
                  <DButton
                    @label={{button.label}}
                    @translatedLabel={{button.translatedLabel}}
                    @icon={{button.icon}}
                    class={{concatClass "btn-transparent" button.className}}
                    @action={{fn this.onExtraButtonAction button.action}}
                  />
                </dropdown.item>
              {{/each}}
            {{/if}}
          </DropdownMenu>
        </:content>
      </DMenu>
    {{/if}}
  </template>
}
